import { ApiClient } from '../ApiClient';
import { createWebSocketUrl } from '../api';

export interface PlaceBidRequest {
  auctionId: string;
  amount: number;
}

export interface BidResponse {
  id: string;
  auctionId: string;
  bidderId: string;
  amount: number;
  placedAt: string;
  status: string;
}

export interface BidHistoryResponse {
  bidderId: string;
  amount: number;
  placedAt: string;
  status: string;
}

export interface AuctionInfo {
  auctionId: string;
  currentPrice: number;
  minimumNextBid: number;
  status: string;
  startsAt: string;
  endsAt: string;
  isActive: boolean;
  winningPrice?: number;
}

export interface AuctionStatus {
  hasAuction: boolean;
  isActive: boolean;
}

export class BiddingAPI {
  private static readonly BASE_URL = '/api/bids';
  private static readonly LISTING_BASE_URL = '/api/public/listings';
  private static readonly MINIMUM_INCREMENT = 1;

  private static normalizeAuctionInfo(response: Partial<AuctionInfo> & { active?: boolean }): AuctionInfo {
    const currentPrice = Number(response.currentPrice ?? 0);
    const minimumNextBid = Number(
      response.minimumNextBid ??
      (Number.isFinite(currentPrice) ? currentPrice + this.MINIMUM_INCREMENT : this.MINIMUM_INCREMENT)
    );

    return {
      auctionId: response.auctionId ?? '',
      currentPrice,
      minimumNextBid,
      status: response.status ?? 'PENDING',
      startsAt: response.startsAt ?? '',
      endsAt: response.endsAt ?? '',
      isActive: response.isActive ?? response.active ?? response.status === 'ACTIVE',
      winningPrice: response.winningPrice,
    };
  }

  /**
   * Places a bid on an auction
   */
  static async placeBid(request: PlaceBidRequest): Promise<BidResponse> {
    try {
      const response = await ApiClient.post<BidResponse>(
        `${this.BASE_URL}`,
        request,
        {
          requiresAuth: true,
        }
      );

      return response;
    } catch (error) {
      console.error('Failed to place bid:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to place bid');
    }
  }

  /**
   * Gets bid details by ID
   */
  static async getBidDetails(bidId: string): Promise<BidResponse> {
    try {
      const response = await ApiClient.get<BidResponse>(
        `${this.BASE_URL}/${bidId}`,
        {
          requiresAuth: false,
        }
      );

      return response;
    } catch (error) {
      console.error('Failed to get bid details:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to get bid details');
    }
  }

  /**
   * Gets bid history for an auction
   */
  static async getBidHistory(
    auctionId: string,
    limit: number = 20,
    offset: number = 0
  ): Promise<BidHistoryResponse[]> {
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString(),
      });

      const response = await ApiClient.get<BidHistoryResponse[]>(
        `${this.BASE_URL}/auction/${auctionId}?${params}`,
        {
          requiresAuth: false,
        }
      );

      return response;
    } catch (error) {
      console.error('Failed to get bid history:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to get bid history');
    }
  }

  /**
   * Gets user's bid history
   */
  static async getUserBidHistory(
    limit: number = 20,
    offset: number = 0
  ): Promise<BidHistoryResponse[]> {
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString(),
      });

      const response = await ApiClient.get<BidHistoryResponse[]>(
        `${this.BASE_URL}/user/history?${params}`,
        {
          requiresAuth: true,
        }
      );

      return response;
    } catch (error) {
      console.error('Failed to get user bid history:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to get user bid history');
    }
  }

  /**
   * Gets auction information for a listing
   */
  static async getListingAuction(listingId: string): Promise<AuctionInfo> {
    try {
      const response = await ApiClient.get<AuctionInfo & { active?: boolean }>(
        `${this.LISTING_BASE_URL}/${listingId}/auction`,
        {
          requiresAuth: false,
        }
      );

      return this.normalizeAuctionInfo(response);
    } catch (error) {
      console.error('Failed to get listing auction:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to get listing auction');
    }
  }

  /**
   * Checks auction status for a listing
   */
  static async getListingAuctionStatus(listingId: string): Promise<AuctionStatus> {
    try {
      const response = await ApiClient.get<AuctionStatus>(
        `${this.LISTING_BASE_URL}/${listingId}/auction/status`,
        {
          requiresAuth: false,
        }
      );

      return response;
    } catch (error) {
      console.error('Failed to get auction status:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to get auction status');
    }
  }

  /**
   * Formats currency from number to display string
   */
  static formatCurrency(amount: number): string {
    const normalizedAmount = Number.isFinite(amount) ? amount : 0;
    return `US $${normalizedAmount.toFixed(2)}`;
  }

  static getWebSocketUrl(): string {
    return createWebSocketUrl('/ws/bidding');
  }

  /**
   * Calculates time remaining until auction ends
   */
  static getTimeRemaining(endsAt: string): string {
    const endTime = new Date(endsAt);
    const now = new Date();
    const diff = endTime.getTime() - now.getTime();

    if (diff <= 0) {
      return 'Ended';
    }

    const totalSeconds = Math.floor(diff / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  }
}
